/**
 * Accept-by deadline for a still-requested booking.
 * Process v5+ expire is min(request+PT48H, booking-start − 1h).
 * Older hosted process used P6D, so request+6d vs start−1h.
 * Last-minute scheduled requests (already inside that 1h lead) fall back
 * to start−15min, then start. New Book a time slots inside the 1h lead
 * are blocked and pointed at Talk now.
 */

import { CANCEL_FREE_NOTICE_MS } from './cancelPolicy';
import { bookingTimes, displayClockToInstant, instantFrom } from './sessionClock';
import { listingTimeZone as listingTimeZoneName } from './timeZone';

const bookingRecord = tx => tx?.booking?.attributes || tx?.booking || {};

const listingTimeZone = tx =>
  listingTimeZoneName(tx?.listing?.attributes?.availabilityPlan?.timezone, 'America/New_York');

/**
 * Session start for accept-by / expire. Never later than the listing-local
 * display clock — Sam's 8 AM booking stored start as 9 AM after an EST/EDT miss.
 *
 * @param {Object} [tx]
 * @returns {number}
 */
export const sessionStartMs = tx => {
  const booking = bookingRecord(tx);
  const zone = listingTimeZone(tx);
  const fromStart = instantFrom(booking.start)?.getTime();
  const fromDisplay = displayClockToInstant(booking.displayStart, zone)?.getTime();
  const fromTimes = bookingTimes(tx).start?.getTime();
  const candidates = [fromStart, fromDisplay, fromTimes].filter(Number.isFinite);
  return candidates.length ? Math.min(...candidates) : NaN;
};

export const ACCEPT_WINDOW_V5_MS = 48 * 60 * 60 * 1000;
export const ACCEPT_WINDOW_LEGACY_MS = 6 * 24 * 60 * 60 * 1000;
export const ACCEPT_WINDOW_V5_MIN_PROCESS_VERSION = 5;

/** Listener must accept this long before session start. Same as free-cancel notice. */
export const ACCEPT_BEFORE_START_MS = CANCEL_FREE_NOTICE_MS;

/** Fallback when the request is already inside ACCEPT_BEFORE_START_MS. */
export const ACCEPT_LAST_MINUTE_BEFORE_START_MS = 15 * 60 * 1000;

const CONFIRM_PAYMENT = 'transition/confirm-payment';

/**
 * Viewer IANA zone, or UTC if Intl is missing.
 *
 * @returns {string}
 */
export const viewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

/**
 * True when this sale still runs the pre-v5 6-day accept window.
 * Missing processVersion is treated as current (48h). Inbox/Dashboard
 * list queries used to omit the field, which made v5+ sales look like
 * the old 6-day window and showed booking start instead of request+48h.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isLegacyAcceptWindow = tx => {
  const version = Number(tx?.attributes?.processVersion);
  if (!Number.isFinite(version)) {
    return false;
  }
  return version < ACCEPT_WINDOW_V5_MIN_PROCESS_VERSION;
};

/**
 * When the request entered preauthorized (confirm-payment).
 *
 * @param {Object} [tx]
 * @returns {number}
 */
export const acceptRequestAtMs = tx => {
  const transitions = tx?.attributes?.transitions;
  if (Array.isArray(transitions)) {
    const paid = transitions.find(item => {
      const name = typeof item === 'string' ? item : item?.transition;
      return name === CONFIRM_PAYMENT;
    });
    const fromTransition = Date.parse(paid?.createdAt);
    if (Number.isFinite(fromTransition)) {
      return fromTransition;
    }
  }
  const last = tx?.attributes?.lastTransition;
  if (last === CONFIRM_PAYMENT) {
    const at = Date.parse(tx?.attributes?.lastTransitionedAt);
    if (Number.isFinite(at)) {
      return at;
    }
  }
  const created = instantFrom(tx?.attributes?.createdAt);
  if (created) {
    return created.getTime();
  }
  const lastAt = Date.parse(tx?.attributes?.lastTransitionedAt);
  return Number.isFinite(lastAt) ? lastAt : NaN;
};

/**
 * Earliest of request+window and session-start − 1h. If that 1h clamp is
 * already at or before the request, use start − 15min, then start.
 *
 * @param {Object} [params]
 * @param {number} [params.requestedAtMs]
 * @param {number} [params.startMs]
 * @param {number} [params.windowMs]
 * @returns {number}
 */
export const acceptDeadlineFromParts = ({ requestedAtMs, startMs, windowMs } = {}) => {
  const plusWindow =
    Number.isFinite(requestedAtMs) && Number.isFinite(windowMs) ? requestedAtMs + windowMs : NaN;
  if (!Number.isFinite(startMs)) {
    return plusWindow;
  }
  const beforeStart = startMs - ACCEPT_BEFORE_START_MS;
  const lastMinute = startMs - ACCEPT_LAST_MINUTE_BEFORE_START_MS;
  let sessionClamp = beforeStart;
  if (Number.isFinite(requestedAtMs) && beforeStart <= requestedAtMs) {
    sessionClamp = lastMinute > requestedAtMs ? lastMinute : startMs;
  }
  const candidates = [plusWindow, sessionClamp].filter(Number.isFinite);
  return candidates.length ? Math.min(...candidates) : NaN;
};

/**
 * Earliest of request+window and session-start − 1h (with last-minute fallback).
 *
 * @param {Object} [tx]
 * @returns {number}
 */
export const acceptDeadlineMs = tx => {
  const requestedAt = acceptRequestAtMs(tx);
  const start = sessionStartMs(tx);
  const windowMs = isLegacyAcceptWindow(tx) ? ACCEPT_WINDOW_LEGACY_MS : ACCEPT_WINDOW_V5_MS;
  return acceptDeadlineFromParts({ requestedAtMs: requestedAt, startMs: start, windowMs });
};

/**
 * True when a scheduled Book a time start is inside the 1-hour accept lead.
 * Talk now is the path for those times.
 *
 * @param {number} startMs
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isScheduledStartTooSoon = (startMs, nowMs = Date.now()) => {
  if (!Number.isFinite(startMs) || !Number.isFinite(nowMs)) {
    return false;
  }
  return startMs - nowMs < ACCEPT_BEFORE_START_MS;
};

/**
 * Earliest instant a new scheduled booking may start.
 *
 * @param {number} [nowMs]
 * @returns {number}
 */
export const minScheduledStartMs = (nowMs = Date.now()) => nowMs + ACCEPT_BEFORE_START_MS;

const ymdInZone = (ms, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));
  const map = Object.fromEntries(
    parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value])
  );
  return `${map.year}-${map.month}-${map.day}`;
};

const addCalendarDaysYmd = (ymd, days) => {
  const [year, month, day] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  const y = utc.getUTCFullYear();
  const m = `${utc.getUTCMonth() + 1}`.padStart(2, '0');
  const d = `${utc.getUTCDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const clockInZone = (ms, timeZone) =>
  new Date(ms)
    .toLocaleTimeString('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    })
    .replace(/\s+/g, ' ')
    .trim();

/**
 * "7:30 PM today", "7:30 PM tomorrow", or "Tue Sep 29, 7:30 PM".
 *
 * @param {number} deadlineMs
 * @param {Object} [opts]
 * @param {number} [opts.nowMs]
 * @param {string} [opts.timeZone]
 * @returns {string}
 */
export const formatAcceptDeadline = (deadlineMs, { nowMs = Date.now(), timeZone } = {}) => {
  if (!Number.isFinite(deadlineMs)) {
    return '';
  }
  const zone = timeZone || viewerTimeZone();
  const today = ymdInZone(nowMs, zone);
  const deadlineDay = ymdInZone(deadlineMs, zone);
  const time = clockInZone(deadlineMs, zone);
  if (deadlineDay === today) {
    return `${time} today`;
  }
  if (deadlineDay === addCalendarDaysYmd(today, 1)) {
    return `${time} tomorrow`;
  }
  const weekday = new Date(deadlineMs).toLocaleDateString('en-US', {
    timeZone: zone,
    weekday: 'short',
  });
  const monthDay = new Date(deadlineMs).toLocaleDateString('en-US', {
    timeZone: zone,
    month: 'short',
    day: 'numeric',
  });
  return `${weekday} ${monthDay}, ${time}`.replace(/\s+/g, ' ').trim();
};

/**
 * Formatted accept-by label for a transaction, or empty.
 *
 * @param {Object} [tx]
 * @param {Object} [opts]
 * @param {number|Date} [opts.now]
 * @param {string} [opts.timeZone]
 * @returns {string}
 */
export const acceptDeadlineLabel = (tx, { now = Date.now(), timeZone } = {}) => {
  const nowMs = now instanceof Date ? now.getTime() : Number(now) || Date.now();
  return formatAcceptDeadline(acceptDeadlineMs(tx), { nowMs, timeZone });
};
